// Cycling entry validation — pure rules used by the web app (pre-submit UX) and
// enforced authoritatively by the `submitCyclingEntry` Cloud Function. The cycling
// mirror of validation.ts: there is no "best N of many" — the stage presents
// exactly CONFIG.CYCLING.DUELS_PER_STAGE balanced duels and you predict EVERY one,
// spreading the confidence ladder across them as a permutation.
import { CONFIG } from './config.js';
const ok = { ok: true };
const fail = (error) => ({ ok: false, error });
const SIDES = ['A', 'B'];
/** Validate a single duel pick's side + confidence shape (not its membership). */
export function validateDuelPick(p) {
    if (!p || !p.duelId)
        return fail('Duel pick is missing a duelId.');
    if (!SIDES.includes(p.pick))
        return fail(`Invalid side for duel ${p.duelId} (must be A or B).`);
    if (typeof p.confidenceWeight !== 'number' || !Number.isFinite(p.confidenceWeight))
        return fail(`Invalid confidence weight for duel ${p.duelId}.`);
    return ok;
}
/**
 * The assigned confidence weights must be exactly CONFIG.CYCLING.CONFIDENCE_WEIGHTS
 * — each value used once, none missing, none repeated (the only fairness lever).
 */
export function validateCyclingConfidence(picks) {
    const expected = [...CONFIG.CYCLING.CONFIDENCE_WEIGHTS].sort((a, b) => a - b);
    const got = picks.map((p) => p.confidenceWeight).sort((a, b) => a - b);
    if (got.length !== expected.length)
        return fail('Confidence weights count mismatch.');
    for (let i = 0; i < expected.length; i++) {
        if (got[i] !== expected[i])
            return fail(`Confidence weights must be a permutation of [${expected.join(', ')}].`);
    }
    return ok;
}
/**
 * Validate a full cycling stage entry against the round (whose `fixtureIds` are the
 * duel ids):
 *  - exactly `round.picksRequired` (= DUELS_PER_STAGE) picks — every duel predicted,
 *  - no duplicate duel, none missing, every duel one of the stage's duels,
 *  - each side is A or B,
 *  - confidence weights are a permutation of CONFIG.CYCLING.CONFIDENCE_WEIGHTS.
 */
export function validateCyclingEntry(duelPicks, round) {
    if (!Array.isArray(duelPicks))
        return fail('Duel picks must be an array.');
    const required = round.picksRequired; // = CONFIG.CYCLING.DUELS_PER_STAGE
    if (duelPicks.length !== required)
        return fail(`Predict all ${required} duels (got ${duelPicks.length}).`);
    const allowed = new Set(round.fixtureIds);
    const seen = new Set();
    for (const p of duelPicks) {
        const m = validateDuelPick(p);
        if (!m.ok)
            return m;
        if (seen.has(p.duelId))
            return fail(`Duplicate pick for duel ${p.duelId}.`);
        seen.add(p.duelId);
        if (allowed.size && !allowed.has(p.duelId))
            return fail(`Duel ${p.duelId} is not in this stage.`);
    }
    // Every duel on the slate must be predicted (none skipped).
    if (allowed.size) {
        for (const id of allowed)
            if (!seen.has(id))
                return fail(`Missing a pick for duel ${id}.`);
    }
    return validateCyclingConfidence(duelPicks);
}
//# sourceMappingURL=cyclingValidation.js.map